/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { Alert } from "@mui/material";
import { styled } from "@mui/material/styles";
import InfoIcon from "@mui/icons-material/Info";
import WarningIcon from "@mui/icons-material/Warning";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { ManageUrgencyType, NotificationListEventsInner } from "client";
import { NotificationDetailDialog } from "components/Notification/NotificationDetailDialog";
import { Const } from "const";
import moment from "moment";

export interface NotificationBarProps {
  notification?: NotificationListEventsInner;
  onClose?: () => void;
}

export const NotificationBar: FC<NotificationBarProps> = ({
  notification,
  onClose,
}) => {
  const { t } = useTranslation(["monitor", "notification"]);
  const [openDetail, setOpenDetail] = useState<NotificationListEventsInner>();
  if (!notification) return null;
  const isAlert = notification.urgency === ManageUrgencyType.Urgent;

  const dateStr = moment(notification.notifiedAt).format(Const.dateTimeFormat);
  const eventId = `[${notification.eventId}]`;
  const title = t(`AWV-UI-SUBJECT-${notification.eventId!}`, {
    ns: "notification",
  });
  const message = t(`AWV-UI-BODY-${notification.eventId!}`, {
    ns: "notification",
    frameTypeAndNumber: notification.frameTypeAndNumber,
    taskId: notification.taskIdDisp,
  });
  return (
    <>
      <NotificationAlert
        variant="filled"
        severity={isAlert ? "error" : "info"}
        icon={isAlert ? <WarningIcon /> : <InfoIcon />}
        onClose={(event) => {
          event.stopPropagation();
          onClose?.();
        }}
        onClick={() => {
          setOpenDetail(notification);
        }}
      >
        {`${dateStr} ${eventId} ${title} ${message}`}
      </NotificationAlert>
      <NotificationDetailDialog
        open={!!openDetail}
        onClose={() => {
          setOpenDetail(undefined);
        }}
        notification={notification}
      />
    </>
  );
};

const NotificationAlert = styled(Alert)(({ theme, severity }) => ({
  alignItems: "center",
  backgroundColor:
    severity === "error" || severity === "warning"
      ? theme.palette.error.dark
      : theme.palette.success.dark,
  borderRadius: 0,
  color: theme.palette.common.white,
  ".MuiAlert-message": {
    fontSize: theme.typography.body1.fontSize,
    fontWeight: "normal",
  },
}));
